// Floating "Clients OnRamp Concierge" voice widget.
// Uses Vapi Web SDK — no server call needed; browser microphone → AI voice.
// Also offers a direct tel: link fallback to +1 562-574-3752.

import { useEffect, useRef, useState } from "react";
import Vapi from "@vapi-ai/web";
import { Phone, PhoneOff, Loader2, MicOff, Mic } from "lucide-react";

const PUBLIC_KEY = process.env.REACT_APP_VAPI_PUBLIC_KEY;
const ASSISTANT_ID = process.env.REACT_APP_VAPI_ASSISTANT_ID;
const TEL_NUMBER = "+15625743752";
const TEL_DISPLAY = "(562) 574-3752";

// Transient assistant config — the concierge script.
// This is loaded per-session, so we can tune the pitch here without touching Vapi dashboard.
const CONCIERGE_ASSISTANT = {
  name: "ClientsOnRamp Concierge",
  firstMessage:
    "Hi there — this is Ava from Clients OnRamp. How can I help you today? I can answer questions about our free website offer or connect you with a person.",
  model: {
    provider: "openai",
    model: "gpt-4o-mini",
    temperature: 0.65,
    messages: [
      {
        role: "system",
        content: `You are Ava, the friendly concierge for Clients OnRamp — a service that gives small local businesses (plumbers, dentists, HVAC, lawyers, insurance agents) a FREE professionally designed website, then upsells a monthly package that includes an AI receptionist, missed-call text-back, and lead capture.

Voice: warm, casual, curious — like a helpful neighbor. Never pushy. Keep answers under 2 sentences unless the caller asks for detail.

Key info to answer questions:
- Free custom website in 24 hours — no credit card required to start.
- After free site, three plans: Starter $397/mo, Front Office $697/mo, Multi-Location $997/mo. All include AI chat widget on the site + automated follow-ups.
- Setup fee waived if they lock in during the first call.
- Serves the US and Canada.
- Add-ons available: AI Voice Receptionist ($99/mo), Missed-Call Text-Back ($29/mo), Multi-page SEO site ($19/mo), Google Reviews widget ($19/mo), and more.
- Target industries: plumbing, HVAC, dentistry, law, insurance, real estate. Open to others.
- If they want to talk to a person: tell them to call our main line at 5-6-2, 5-7-4, 3-7-5-2 (say it slowly), or the founder will call them back within one business day.
- Book a 15-minute demo whenever they show interest.

If asked something you don't know (like specific technical questions), say "great question — let me have someone from the team follow up on that." Never invent facts.

If they want to book a demo, take their business name, phone number, and a preferred time. Confirm you'll send a calendar invite.

End the call politely with: "Thanks so much for calling Clients OnRamp — have a great day!"`,
      },
    ],
  },
  voice: { provider: "11labs", voiceId: "sarah" },
  endCallMessage: "Thanks so much for calling Clients OnRamp — talk soon!",
  silenceTimeoutSeconds: 20,
  maxDurationSeconds: 420,
};

export default function VapiConcierge() {
  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState("idle"); // idle | connecting | live | ending
  const [muted, setMuted] = useState(false);
  const [transcript, setTranscript] = useState([]); // {role, text}
  const [error, setError] = useState(null);
  const vapiRef = useRef(null);

  useEffect(() => {
    if (!PUBLIC_KEY) return;
    const vapi = new Vapi(PUBLIC_KEY);
    vapiRef.current = vapi;

    vapi.on("call-start", () => setStatus("live"));
    vapi.on("call-end", () => {
      setStatus("idle");
      setMuted(false);
    });
    vapi.on("error", (e) => {
      console.error("Vapi error", e);
      setError(e?.errorMsg || e?.message || "Voice error — try again");
      setStatus("idle");
    });
    vapi.on("message", (msg) => {
      if (msg.type === "transcript" && msg.transcriptType === "final") {
        setTranscript((prev) => [...prev, { role: msg.role, text: msg.transcript }]);
      }
    });

    return () => {
      try { vapi.stop(); } catch { /* ignore */ }
    };
  }, []);

  const startCall = async () => {
    setError(null);
    setTranscript([]);
    if (!vapiRef.current) {
      setError("Concierge not initialized");
      return;
    }
    setStatus("connecting");
    try {
      // Prefer the Vapi dashboard assistant so edits in the dashboard flow
      // through automatically. Fallback to inline transient config if unset.
      if (ASSISTANT_ID) {
        await vapiRef.current.start(ASSISTANT_ID);
      } else {
        await vapiRef.current.start(CONCIERGE_ASSISTANT);
      }
    } catch (e) {
      setError(String(e?.message || e));
      setStatus("idle");
    }
  };

  const endCall = () => {
    setStatus("ending");
    try { vapiRef.current?.stop(); } catch { /* ignore */ }
  };

  const toggleMute = () => {
    const next = !muted;
    setMuted(next);
    try { vapiRef.current?.setMuted(next); } catch { /* ignore */ }
  };

  return (
    <div
      className="vapi-concierge"
      style={{ position: "fixed", left: 24, bottom: 24, zIndex: 10000, fontFamily: "'IBM Plex Sans',system-ui,sans-serif" }}
      data-testid="vapi-concierge-widget"
    >
      {!open ? (
        <button
          onClick={() => setOpen(true)}
          data-testid="vapi-concierge-open"
          className="cor-concierge-fab"
          aria-label="Open Clients OnRamp concierge"
          style={{
            background: "#E24E1B",
            color: "white",
            border: "none",
            borderRadius: 999,
            padding: "14px 22px 14px 18px",
            display: "flex",
            alignItems: "center",
            gap: 10,
            fontWeight: 700,
            fontSize: 15,
            cursor: "pointer",
            boxShadow: "0 8px 32px rgba(226,78,27,0.35), 0 2px 8px rgba(0,0,0,0.15)",
            transition: "transform 0.15s ease",
          }}
          onMouseEnter={(e) => (e.currentTarget.style.transform = "scale(1.04)")}
          onMouseLeave={(e) => (e.currentTarget.style.transform = "scale(1)")}
        >
          <Phone className="w-5 h-5" />
          Clients OnRamp Concierge
        </button>
      ) : (
        <div
          style={{
            width: 340,
            background: "#0F2340",
            color: "white",
            borderRadius: 12,
            boxShadow: "0 24px 64px rgba(0,0,0,0.45)",
            overflow: "hidden",
          }}
        >
          {/* Header */}
          <div style={{ background: "#E24E1B", padding: "12px 16px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div>
              <div style={{ fontSize: 11, letterSpacing: 2, textTransform: "uppercase", opacity: 0.85 }}>Live voice AI</div>
              <div style={{ fontWeight: 700 }}>Concierge · Ava</div>
            </div>
            <button
              onClick={() => { endCall(); setOpen(false); }}
              data-testid="vapi-concierge-close"
              style={{ background: "transparent", color: "white", border: "none", fontSize: 22, cursor: "pointer" }}
              aria-label="Close"
            >×</button>
          </div>

          <div style={{ padding: 20 }}>
            {status === "idle" && !error && (
              <>
                <div style={{ fontSize: 15, lineHeight: 1.5, marginBottom: 16 }}>
                  Ask me anything about your free website, pricing, or the AI receptionist. I&apos;ll answer in real time.
                </div>
                <button
                  onClick={startCall}
                  data-testid="vapi-concierge-start"
                  style={{ width: "100%", background: "#E24E1B", color: "white", border: "none", padding: "12px", fontWeight: 700, cursor: "pointer", borderRadius: 6, fontSize: 15 }}
                >
                  <Mic className="w-4 h-4 inline mr-2" /> Start voice chat
                </button>
                <div style={{ marginTop: 14, fontSize: 12, opacity: 0.7, textAlign: "center" }}>
                  Prefer to talk to a person?
                </div>
                <a
                  href={`tel:${TEL_NUMBER}`}
                  data-testid="vapi-concierge-tel"
                  style={{ display: "block", textAlign: "center", marginTop: 8, color: "white", textDecoration: "underline", fontSize: 14 }}
                >
                  Call {TEL_DISPLAY}
                </a>
              </>
            )}

            {status === "connecting" && (
              <div style={{ textAlign: "center", padding: 24 }}>
                <Loader2 className="w-6 h-6 animate-spin inline" />
                <div style={{ marginTop: 12, fontSize: 14 }}>Connecting…</div>
                <div style={{ marginTop: 6, fontSize: 12, opacity: 0.7 }}>Allow microphone if prompted</div>
              </div>
            )}

            {status === "live" && (
              <>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8, marginBottom: 12 }}>
                  <span style={{ width: 8, height: 8, borderRadius: 999, background: "#22c55e", boxShadow: "0 0 12px #22c55e" }} />
                  <span style={{ fontSize: 13, fontWeight: 600 }}>Live · speaking with Ava</span>
                </div>
                <div style={{
                  maxHeight: 200, overflowY: "auto",
                  background: "rgba(255,255,255,0.05)",
                  padding: 12, borderRadius: 6, marginBottom: 14,
                  fontSize: 13, lineHeight: 1.5,
                }}>
                  {transcript.length === 0 ? (
                    <div style={{ opacity: 0.6, fontStyle: "italic" }}>Say something to start…</div>
                  ) : (
                    transcript.slice(-6).map((t, i) => (
                      <div key={i} style={{ marginBottom: 8 }}>
                        <span style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: 1.5, opacity: 0.6 }}>
                          {t.role === "assistant" ? "Ava" : "You"}
                        </span>
                        <div>{t.text}</div>
                      </div>
                    ))
                  )}
                </div>
                <div style={{ display: "flex", gap: 8 }}>
                  <button
                    onClick={toggleMute}
                    data-testid="vapi-concierge-mute"
                    style={{
                      flex: 1, background: muted ? "#f59e0b" : "rgba(255,255,255,0.15)",
                      color: "white", border: "none", padding: 10, cursor: "pointer",
                      borderRadius: 6, fontSize: 13, fontWeight: 600,
                    }}
                  >
                    {muted ? <><MicOff className="w-4 h-4 inline mr-1" /> Unmute</> : <><Mic className="w-4 h-4 inline mr-1" /> Mute</>}
                  </button>
                  <button
                    onClick={endCall}
                    data-testid="vapi-concierge-end"
                    style={{ flex: 1, background: "#ef4444", color: "white", border: "none", padding: 10, cursor: "pointer", borderRadius: 6, fontSize: 13, fontWeight: 700 }}
                  >
                    <PhoneOff className="w-4 h-4 inline mr-1" /> End
                  </button>
                </div>
              </>
            )}

            {status === "ending" && (
              <div style={{ textAlign: "center", padding: 24, fontSize: 14 }}>Ending…</div>
            )}

            {error && (
              <div style={{ padding: 12, background: "rgba(239,68,68,0.15)", borderRadius: 6, fontSize: 13, marginTop: 10 }}>
                {error}
                <button
                  onClick={() => { setError(null); setStatus("idle"); }}
                  style={{ marginLeft: 8, textDecoration: "underline", color: "white", background: "none", border: "none", cursor: "pointer" }}
                >
                  retry
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
